/**
 * Clave de idempotencia para un toque del carrito (`Idempotency-Key`).
 *
 * El backend acepta hasta 64 caracteres y devuelve el mismo resultado si la
 * misma clave llega dos veces (BE #625, RF-007): así un reintento por wifi
 * flojo no suma dos veces. `crypto.randomUUID` no existe en todos los WebView
 * de tablet ni en jsdom; el repuesto tiene la misma forma de unicidad práctica.
 */
export function nuevaClaveIdempotencia() {
  const c = typeof window !== 'undefined' ? window.crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const azar = () => Math.random().toString(36).slice(2, 10);
  return `${Date.now().toString(36)}-${azar()}-${azar()}-${azar()}`;
}

// Un hueco por venue y por ÁMBITO (la pantalla desde la que se cobra). Con un solo
// hueco por venue, cobrar desde Carritos pisaba la clave del Dispenser y viceversa: el
// reintento del primero generaba clave NUEVA y volvía a cobrar. Sin ámbito se conserva
// el nombre de siempre, para no invalidar la clave que el Dispenser ya tenga guardada.
const CLAVE_COBRO_STORAGE = (venueId, ambito) =>
  ambito ? `claveCobro_${venueId}_${ambito}` : `claveCobro_${venueId}`;

/**
 * Clave de idempotencia del COBRO para «este carrito de este socio» (`huella`).
 *
 * Misma huella → misma clave, también tras recargar la pantalla: la pareja
 * (huella, clave) se guarda en localStorage por venue. Huella distinta → clave
 * nueva y se sobrescribe. Sin storage (modo privado, jsdom sin permiso) funciona
 * igual, solo que la clave no sobrevive a la recarga.
 */
// Respaldo en memoria para cuando `localStorage` no está (modo privado, WebView con
// datos bloqueados, cuota llena). Sin él, en esas tablets CADA intento generaba clave
// nueva y el reintento cobraba otra vez — justo el bug del que va todo esto. No
// sobrevive a recargar la página, pero sí al reintento dentro de la misma pantalla,
// que es el caso que más ocurre.
const clavesEnMemoria = new Map();

export function claveCobroParaHuella(venueId, huella, ambito) {
  const k = CLAVE_COBRO_STORAGE(venueId, ambito);
  try {
    const guardada = JSON.parse(localStorage.getItem(k) || 'null');
    if (guardada && guardada.huella === huella && guardada.clave) return guardada.clave;
  } catch { /* storage inaccesible o corrupto: se mira el respaldo */ }
  const enMemoria = clavesEnMemoria.get(k);
  if (enMemoria && enMemoria.huella === huella) return enMemoria.clave;

  const clave = nuevaClaveIdempotencia();
  clavesEnMemoria.set(k, { huella, clave });
  try { localStorage.setItem(k, JSON.stringify({ huella, clave })); } catch { /* sin storage */ }
  return clave;
}

/**
 * Deja escrito que ESTA clave es la de ESTA huella, sin acuñar ninguna.
 *
 * Hace falta cuando la clave está congelada a propósito —el Dispenser la fija mientras
 * el cobro está en vuelo— y el carrito cambia por debajo. Sin esto, lo guardado sigue
 * apuntando a la huella vieja: al descongelar no casa, se acuña clave nueva y el
 * reintento deja de ser un reintento. Congelar sin re-anclar es peor que no congelar.
 */
export function fijarClaveCobro(venueId, huella, clave, ambito) {
  if (!clave) return;
  const k = CLAVE_COBRO_STORAGE(venueId, ambito);
  clavesEnMemoria.set(k, { huella, clave });
  try { localStorage.setItem(k, JSON.stringify({ huella, clave })); } catch { /* sin storage */ }
}

/** Tras un cobro hecho, la pareja guardada ya no vale para nada: fuera. */
export function olvidarClaveCobro(venueId, ambito) {
  const k = CLAVE_COBRO_STORAGE(venueId, ambito);
  clavesEnMemoria.delete(k);
  try { localStorage.removeItem(k); } catch { /* sin storage */ }
}

/**
 * El cobro, en UN solo sitio: clave de idempotencia + corte de espera.
 *
 * Kong House lo reportó el 13-09-2026 («se me han puesto 2 veces la misma
 * dispensación») y se arregló SOLO en la pantalla desde la que lo vieron. Las demás
 * quedaron anotadas como deuda y el 24-09 volvió a pasar por otra de ellas. Por eso
 * esto deja de ser una cabecera que cada pantalla se acuerda de poner.
 *
 * `huella` = qué se está cobrando (carrito + socio). Misma huella → misma clave, así
 * que un reintento —red caída, modal reabierto, recarga— devuelve la venta ya hecha
 * en vez de cobrar otra vez. `ambito` = desde qué pantalla, para que dos pantallas
 * abiertas no se pisen la clave.
 */
export const COBRO_TOPE_MS = 15000;

export async function enviarCobro({ fetchImpl, token, venueId, ambito, huella, cuerpo, claveYaCalculada }) {
  // El Dispenser calcula su clave antes (la recuerda por carrito mientras el camarero
  // trastea) y la pasa hecha; el resto la pide aquí por huella.
  const clave = claveYaCalculada || claveCobroParaHuella(venueId, huella, ambito);
  // El ámbito viaja también al servidor, que lo guarda en el apunte de /actividad. Es el
  // dato que no existía cuando Kong reportó la dispensación duplicada del 24-09-2026:
  // `cart_type` decía «dispensario» y eso lo mandan dos pantallas distintas, así que no
  // hubo forma de saber de cuál salió.
  const cuerpoConOrigen = ambito ? { ...cuerpo, origen: ambito } : cuerpo;
  const corte = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const tope = corte ? setTimeout(() => corte.abort(), COBRO_TOPE_MS) : null;
  try {
    return await (fetchImpl || fetch)('/v1/make-purchase', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Idempotency-Key': clave,
      },
      signal: corte?.signal,
      body: JSON.stringify(cuerpoConOrigen),
    });
  } finally {
    if (tope) clearTimeout(tope);
  }
}

/** El aviso de un cobro que no vuelve. Lo importante es que NO se ha cobrado dos veces. */
export const AVISO_COBRO_LENTO =
  'El cobro está tardando demasiado. Comprueba la red y vuelve a intentarlo: no se cobrará dos veces.';
